import {
  createSignal,
  createEffect,
  JSXElement,
  mergeProps,
  Show,
  splitProps
} from 'solid-js';
import { Button } from '~/components/ui/Button';
import { useFormContext } from '~/components/Form/FormContext';
import { ActionsDropdown } from '~/components/ActionsDropdown';
import delve from 'dlv';
import { CheckIcon, ChevronDown, ChevronUp } from 'lucide-solid';
import { Input, InputProps } from '~/components/ui/Input.tsx';
import styles from '~/components/ui/Forms/input-field.module.scss';

type ComponentProps = {
  label?: JSXElement;
  labelSize?: 'sm' | 'md' | 'lg' | 'xl';
  hint?: JSXElement;
  type?: string;
  menuItems?: string[];
  menuFieldName?: string;
  onMenuItemClick?: (e: string) => void;
};

type ElementProps = InputProps;

type InputMenuFieldProps = ComponentProps & ElementProps;

export default function InputMenuField(props: InputMenuFieldProps): JSXElement {
  const merged = mergeProps(
    {
      menuItems: [],
      disabled: false
    },
    props
  );

  const [inputMenuProps, inputProps] = splitProps(merged, [
    'menuFieldName',
    'menuItems',
    'onMenuItemClick'
  ]);

  const splittedProps = splitProps(inputProps, [
    'label',
    'labelSize',
    'hint',
    'type'
  ]);
  const inputFieldProps = splittedProps[0];
  const inputElementProps: ElementProps = splittedProps[1] as ElementProps;

  const formAccessors = useFormContext();

  const [fieldError, setFieldError] = createSignal<Array<string>>([]);

  const [selectedMenuItem, setSelectedMenuItem] = createSignal(
    (merged.menuFieldName && formAccessors.data()[merged.menuFieldName]) ||
      'Select'
  );

  const testId = `field-${inputProps.name || inputProps.label || new Date().getTime()}`;

  createEffect(() => {
    setSelectedMenuItem(
      (merged.menuFieldName && formAccessors.data()[merged.menuFieldName]) ||
        'Select'
    );
  });

  createEffect(() => {
    if (merged.name) {
      const error = delve(formAccessors?.errors(), merged.name, '');
      if (!!error && error.length) {
        // the setTimeout is added to defer setting the error by 200ms
        setTimeout(
          (fieldName, { errors }) => {
            const fieldError = delve(errors(), fieldName, '');
            if (!!fieldError && fieldError.length) {
              setFieldError(fieldError);
            } else {
              setFieldError([]);
            }
          },
          0,
          merged.name,
          formAccessors
        );
      } else {
        setFieldError([]);
      }
    }
  });

  return (
    <div>
      <div class={`flex gap-2`}>
        <Input
          class={`w-[12rem]`}
          size={'sm'}
          classList={{
            error: !!fieldError().length
          }}
          {...inputElementProps}
          type={inputFieldProps.type}
          data-testid={testId}
        />

        <Show when={inputMenuProps.menuItems?.length > 0}>
          <div class={`self-start w-[6.25rem]`}>
            <ActionsDropdown
              id={`input-field-units`}
              items={((inputMenuProps.menuItems || []) as string[]).map(
                (item: string) => {
                  let isActive = item === selectedMenuItem();
                  return {
                    label: (
                      <div
                        class={`flex items-center justify-between flex-grow ${isActive ? styles.selectedField : ''}`}
                      >
                        <span>{item}</span>
                        <Show when={isActive}>
                          <CheckIcon
                            class={`w-[20px] h-[20px] text-fg-default`}
                          />
                        </Show>
                      </div>
                    ),
                    onClick: () => inputMenuProps.onMenuItemClick!(item)
                  };
                }
              )}
              disabled={
                inputElementProps.disabled ||
                inputMenuProps.menuItems?.length === 0
              }
              containerClass={`${styles.menuFieldContainer}`}
            >
              {(isOpen) => (
                <Button
                  size={'sm'}
                  variant={`outline`}
                  class={`border-border-default min-w-[6.25rem] [&>span:first-child]:flex-grow`}
                  classList={{
                    [styles.error]: !!fieldError().length
                  }}
                >
                  <span class={`font-normal`}>{selectedMenuItem()}</span>
                  <Show
                    when={!isOpen}
                    fallback={<ChevronUp class={`ml-1`} />}
                  >
                    <ChevronDown class={`ml-1`} />
                  </Show>
                </Button>
              )}
            </ActionsDropdown>
          </div>
        </Show>
      </div>
      <Show when={!!fieldError().length}>
        <p class={`text-error text-sm mt-1`}>{fieldError()}</p>
      </Show>
    </div>
  );
}
